import { ArrowDownTrayIcon, EyeIcon } from '@heroicons/react/24/outline';
import download from 'downloadjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CourseAdminCard } from '@/components/shared/CourseAdminCard';
import {
  CardDragglableProps,
  CardStaticProps,
} from '@/components/shared/CourseAdminCard/types';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Button } from '@/components/ui/daisy';
import { downloadLessonResource } from '@/data/services/resourceServices';
import Resource, { ResourceDTO } from '@/models/Resource';
import { getFileExtensionFromUrl } from '@/utils/file';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { getResourceUrl } from '@/utils/resource/getResourceUrl';
import { resourceFieldsToDTO } from '@/utils/resource/resourceFieldsToDTO';
import { sanitizeFileUnchanged } from '@/utils/resource/sanitizeFileUnchanged';
import alert from '@/utils/shared/alert';
import {
  OnUploadProgressFn,
  ServiceUploadingOptions,
} from '@/utils/shared/uploadProgress';
import ResourceBadges from './ResourceBadges';
import ResourceInlineForm, { ResourceFormFields } from './ResourceInlineForm';

type ResourceCardProps = {
  resource: Resource;
  ordinal: number; //TODO: remove when back implement order route
  lessonId: number;
  onUpdate: (params: {
    resourceId: number;
    data: Partial<ResourceDTO>;
    onUploadProgress?: OnUploadProgressFn;
  }) => Promise<Resource>;
  onDelete?: (resourceId: number) => void;
  isUpdating?: boolean;
  className?: string;
} & (CardStaticProps | CardDragglableProps);

export default function ResourceCard({
  resource,
  ordinal,
  lessonId,
  onUpdate,
  onDelete,
  isUpdating,
  ...cardProps
}: ResourceCardProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.resourcesTab',
  });
  const [isEditing, setIsEditing] = useState(false);
  const viewUrl = getResourceUrl(resource);
  const fileUrl =
    typeof resource.resourceFile === 'string' ? resource.resourceFile : null;

  const handleUpdate = async (
    data: ResourceFormFields,
    options: ServiceUploadingOptions,
  ) => {
    const dto = sanitizeFileUnchanged(data, resourceFieldsToDTO(data));
    await onUpdate({
      resourceId: resource.id,
      data: dto,
      onUploadProgress: options.onUploadProgress,
    });
    setIsEditing(false);
  };

  const handleDownload = async () => {
    if (!fileUrl) return;
    try {
      const blob = await downloadLessonResource(lessonId, resource.id);
      const ext = getFileExtensionFromUrl(fileUrl).toLowerCase();
      const filename = ext ? `${resource.title}.${ext}` : resource.title;
      download(blob, filename, blob.type);
    } catch (err) {
      alert.error(getErrorMessage(err));
    }
  };

  if (isEditing) {
    return (
      <div className={cardProps.className}>
        <ResourceInlineForm
          resource={resource}
          onSubmit={handleUpdate}
          onCancel={() => setIsEditing(false)}
          isLoading={isUpdating}
        />
      </div>
    );
  }

  return (
    <CourseAdminCard {...cardProps}>
      <CourseAdminCard.Header>
        <CourseAdminCard.Leading>
          <CourseAdminCard.Order value={ordinal} />
          <CourseAdminCard.Title>{resource.title}</CourseAdminCard.Title>
        </CourseAdminCard.Leading>
        <CourseAdminCard.Actions>
          <ResourceBadges resource={resource} />
          <ConditionalRenderer condition={!!viewUrl}>
            <Button
              type="button"
              variant="soft"
              color="secondary"
              size="sm"
              shape="square"
              onClick={() =>
                viewUrl && window.open(viewUrl, '_blank', 'noopener,noreferrer')
              }
              aria-label={t('viewResource')}
            >
              <EyeIcon className="max-xl:w-3.5 w-4" />
            </Button>
          </ConditionalRenderer>
          <ConditionalRenderer condition={!!fileUrl}>
            <Button
              type="button"
              variant="soft"
              color="info"
              size="sm"
              shape="square"
              onClick={handleDownload}
              aria-label={t('downloadResource')}
            >
              <ArrowDownTrayIcon className="max-xl:w-3.5 w-4" />
            </Button>
          </ConditionalRenderer>
          <CourseAdminCard.EditButton onClick={() => setIsEditing(true)} />
          <ConditionalRenderer condition={!!onDelete}>
            <CourseAdminCard.MoreActions>
              <CourseAdminCard.MoreActions.Delete
                onClick={() => onDelete?.(resource.id)}
              />
            </CourseAdminCard.MoreActions>
          </ConditionalRenderer>
        </CourseAdminCard.Actions>
      </CourseAdminCard.Header>
    </CourseAdminCard>
  );
}
